import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PortfolioUploadDialogComponent } from 'src/app/components/portfolio-upload-dialog/portfolio-upload-dialog.component';
import { UploadResultDialogComponent } from 'src/app/components/upload-result-dialog/upload-result-dialog.component';
import { PdfExportDialogComponent } from 'src/app/components/pdf-export-dialog/pdf-export-dialog.component';
import { EditConstraintsDialogComponent } from 'src/app/components/edit-constraints-dialog/edit-constraints-dialog.component';
import { CompanyModel } from 'src/app/shared/models/company.model';
import { VISIBILITY_SHARED } from 'src/app/shared/models/portfolio.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { PortfolioPollingService } from 'src/app/shared/services/portfolio-polling.service';
import { SparklineComponent } from 'src/app/charts/sparkline/sparkline.component';
import { MaterialModule } from 'src/app/app.materials';
import { ExperienceTheme, ExperienceThemeService } from '../home-experience/services/experience-theme.service';
import { PortfolioService } from './portfolio.service';

interface PortfolioTile {
  id: number;
  name: string;
  status: string;
  ccy: string;
  assets: number;
  starred: boolean;
  risk: number;
  delta: number;
  created: string;
  updated: string;
  createdTs: number;
  updatedTs: number;
  series: number[];
  isLoadingSeries: boolean;
  readonly: boolean;
  visibility: number;
  raw: any;
}

type ViewMode = 'cards' | 'list';
type SortKey = 'updated' | 'return' | 'risk' | 'name';

const INITIAL_PORTFOLIOS_ON_LIST = 12;
const LOAD_MORE_BATCH_SIZE = 6;

@Component({
  selector: 'app-portfolio',
  templateUrl: './portfolio.component.html',
  styleUrls: ['./portfolio.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SparklineComponent,
    MaterialModule,
  ],
  standalone: true,
})
export class PortfolioComponent implements OnInit {
  theme: ExperienceTheme = 'light';

  readonly sharedVisibility = VISIBILITY_SHARED;

  userName = '';
  greeting = 'Good morning';

  isUploading = false;

  all: PortfolioTile[] = [];
  filtered: PortfolioTile[] = [];
  visible: PortfolioTile[] = [];
  visibleCount = INITIAL_PORTFOLIOS_ON_LIST;
  isLoadingMore = false;
  isLoading = true;
  hasError = false;

  search = '';
  view: ViewMode = 'cards';

  currencies: string[] = [];
  ccyFilter = new Set<string>();
  ccyFilterOpen = false;
  favouritesOnly = false;
  sortKey: SortKey = 'updated';
  sortDir: 'asc' | 'desc' = 'desc';

  totalDelta = 0;
  avgRisk = 0;
  winners = 0;
  starredCount = 0;

  private hydratedIds = new Set<number>();
  private inflightIds = new Set<number>();

  constructor(
    private service: PortfolioService,
    private router: Router,
    private dialog: MatDialog,
    private auth: AuthService,
    private themeService: ExperienceThemeService,
    private portfolioPolling: PortfolioPollingService,
  ) {}

  ngOnInit(): void {
    this.theme = this.themeService.current;
    this.themeService.current$.subscribe((t) => (this.theme = t));
    this.greeting = this.computeGreeting();

    this.auth.getCurrentUser().subscribe((user: any) => {
      this.userName = this.resolveUserName(user);
    });

    this.fetch();
    this.listenFinished();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  listenFinished(): void {
    this.service.isPortfolioFinished().subscribe((resp: any) => {
      if (resp && resp.id) {
        this.fetch();
      }
    });
  }

  openPortfolio(p: PortfolioTile): void {
    this.router.navigate(['/app/portfolio-view', p.id]);
  }

  onNewPortfolio(): void {
    this.service.clearUploadState();
  }

  // -- Per-tile actions (restored from the pre-redesign portfolio table menu) --

  onEdit(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/app/portfolio', p.id]);
  }

  onView(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/app/portfolio-view', p.id]);
  }

  onDuplicate(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.service.forkItem(p.id).subscribe((resp: any) => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error);
      }
      this.service.notificationService.open('Portfolio copied to your private list.');
      this.fetch();
    });
  }

  onClickInit(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.doInit(p);
  }

  onEditConstraints(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EditConstraintsDialogComponent, {
      data: { portfolioId: p.id },
      maxWidth: '900px',
      width: '900px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.doInit(p);
      }
    });
  }

  private doInit(p: PortfolioTile): void {
    const portfolio = { ...p.raw };
    if (portfolio.currency && !portfolio.currency_id) {
      portfolio.currency_id = portfolio.currency.id;
    }
    portfolio.needReload = true;
    this.service.saveItem(portfolio, false).subscribe((resp: any) => {
      if (resp.id) {
        this.service.notificationService.open('Portfolio initialization started');
        p.status = 'Computing';
        this.visible = [...this.visible];
        this.startPortfolioPolling(resp.id, p.name);
      }
    });
  }

  onExportPdf(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.dialog.open(PdfExportDialogComponent, {
      data: { portfolioId: p.id, portfolioName: p.name },
      maxWidth: '1200px',
      minWidth: '1100px',
    });
  }

  deleteItem(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.service.confirmAction().subscribe((confirm) => {
      if (confirm) {
        this.doDelete(p);
      }
    });
  }

  unshareItem(p: PortfolioTile, event: Event): void {
    event.stopPropagation();
    this.service.leaveShareItem(p.id).subscribe((resp: any) => {
      if (resp?.error) {
        return this.service.notificationService.open(resp.error);
      }
      this.service.notificationService.open('Share removed');
      this.removeTile(p.id);
    });
  }

  private doDelete(p: PortfolioTile): void {
    this.service.deleteItem({ id: p.id }).subscribe((resp: any) => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error);
      }
      this.service.notificationService.open('Portfolio deleted');
      this.removeTile(p.id);
    });
  }

  private removeTile(id: number): void {
    this.all = this.all.filter((t) => t.id !== id);
    this.computeStats();
    this.applyFilters();
  }

  private startPortfolioPolling(portfolioID: number, portfolioName = ''): void {
    this.portfolioPolling.watchPortfolio(portfolioID, {
      onStatusComplete: () => {
        this.service.notificationService.openWithAction(
          'Portfolio ' + portfolioName + ' computation finished',
          'Open',
          '/app/portfolio-view/' + portfolioID + '/overview',
        );
        this.setTileStatus(portfolioID, 'Ready');
      },
      onStatusFailed: () => {
        this.service.notificationService.openPersistent(
          'No feasible portfolio could be generated with the selected constraints. Please review and adjust the constraints.',
        );
        this.setTileStatus(portfolioID, 'Failed');
      },
      pollingInterval: 6000,
      initialDelay: 10000,
      listenerKey: `portfolio-${portfolioID}`,
      context: this,
    });
  }

  private setTileStatus(id: number, status: string): void {
    const tile = this.all.find((t) => t.id === id);
    if (tile) {
      tile.status = status;
      this.visible = [...this.visible];
    }
  }

  toggleStar(p: PortfolioTile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Optimistic update for a snappy UI; reconciled with the server below.
    const previous = p.starred;
    p.starred = !p.starred;
    this.afterStarChange();

    this.service
      .toggleFavourite(p.id)
      .pipe(catchError(() => of(null)))
      .subscribe((resp: any) => {
        if (!resp || resp.error || typeof resp.is_starred !== 'boolean') {
          // Roll back on failure and surface the error.
          p.starred = previous;
          this.afterStarChange();
          this.service.notificationService.open(
            resp?.error || 'Could not update favourite. Please try again.',
          );
          return;
        }
        // Server is the source of truth.
        p.starred = resp.is_starred;
        this.afterStarChange();
      });
  }

  private afterStarChange(): void {
    this.starredCount = this.all.filter((x) => x.starred).length;
    // Intentionally do NOT re-sort/re-filter here: favouriting a tile in place
    // must not reorder the list. The reshuffle ("jumping") is jarring for users.
    // The new star state is reflected via the in-place p.starred binding;
    // ordering and favourites-only filtering refresh on the next
    // sort/filter/search interaction or fetch.
  }

  setView(v: ViewMode): void {
    this.view = v;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target?.closest('.pflist-filter-wrap')) {
      this.ccyFilterOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.ccyFilterOpen = false;
  }

  toggleCurrencyFilter(): void {
    this.ccyFilterOpen = !this.ccyFilterOpen;
  }

  toggleCurrencyOption(value: string): void {
    const next = new Set(this.ccyFilter);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    this.ccyFilter = next;
    this.resetVisibleCount();
    this.applyFilters();
  }

  clearCurrencyFilter(): void {
    if (!this.ccyFilter.size) return;
    this.ccyFilter = new Set();
    this.resetVisibleCount();
    this.applyFilters();
  }

  toggleFavouritesOnly(): void {
    this.favouritesOnly = !this.favouritesOnly;
    this.resetVisibleCount();
    this.applyFilters();
  }

  get currencyFilterLabel(): string {
    const n = this.ccyFilter.size;
    if (!n) return 'All';
    if (n === 1) return Array.from(this.ccyFilter)[0];
    return `${n} selected`;
  }

  cycleSort(): void {
    const order: SortKey[] = ['updated', 'return', 'risk', 'name'];
    const idx = order.indexOf(this.sortKey);
    if (this.sortDir === 'desc') {
      this.sortDir = 'asc';
    } else {
      this.sortDir = 'desc';
      this.sortKey = order[(idx + 1) % order.length];
    }
    this.resetVisibleCount();
    this.applyFilters();
  }

  get sortLabel(): string {
    const labels: Record<SortKey, string> = {
      updated: 'Last updated',
      return: 'Return',
      risk: 'Risk',
      name: 'Name',
    };
    const arrow = this.sortDir === 'desc' ? '↓' : '↑';
    return `${labels[this.sortKey]} ${arrow}`;
  }

  onSearch(): void {
    this.resetVisibleCount();
    this.applyFilters();
  }

  get hasMorePortfolios(): boolean {
    return this.filtered.length > this.visible.length;
  }

  get remainingPortfolioCount(): number {
    return Math.max(this.filtered.length - this.visible.length, 0);
  }

  onLoadMore(): void {
    if (this.isLoadingMore || !this.hasMorePortfolios) return;
    this.isLoadingMore = true;
    this.visibleCount = Math.min(
      this.filtered.length,
      this.visibleCount + LOAD_MORE_BATCH_SIZE,
    );
    this.visible = this.filtered.slice(0, this.visibleCount);
    this.hydrateVisible();
    this.isLoadingMore = false;
  }

  private resetVisibleCount(): void {
    this.visibleCount = INITIAL_PORTFOLIOS_ON_LIST;
  }

  trackByTile(_i: number, p: PortfolioTile): number {
    return p.id;
  }

  colorFor(p: PortfolioTile): string {
    return p.delta >= 0 ? '#00E5A0' : '#FF5C7A';
  }

  // -- Upload flow (kept from the legacy portfolio page) --------------------

  openUploadDialog(): void {
    const dialogRef = this.dialog.open(PortfolioUploadDialogComponent, {
      width: '92vw',
      maxWidth: '520px',
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((file: File | undefined) => {
      if (file) {
        this.onUploadEvent(file);
      }
    });
  }

  onUploadEvent(file): void {
    if (!file) {
      this.service.notificationService.open('Error. Missing file.');
      return;
    }
    this.isUploading = true;
    const formData = new FormData();
    formData.append('file', file);
    this.service.uploadCSVFile(formData).subscribe((resp: any) => {
      this.isUploading = false;
      if (resp.error) {
        return this.service.notificationService.open(resp.error);
      }

      const result = this.handleResponse(resp);
      this.service.uploadedCompanies = result.companies;
      this.service.uploadedNotFound = result.notFound;

      if (result.notFound && result.notFound.length > 0) {
        const dialogRef = this.dialog.open(UploadResultDialogComponent, {
          data: {
            successCount: result.companies.length,
            notFound: result.notFound,
          },
          maxWidth: '500px',
          disableClose: false,
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            this.router.navigate(['app/portfolio-create/select']);
          }
        });
      } else {
        this.service.notificationService.open(
          `File uploaded! All ${result.companies.length} companies imported.`,
        );
        this.router.navigate(['app/portfolio-create/select']);
      }
    });
  }

  private handleResponse(resp) {
    let companies = [];
    let notFound = [];
    if (resp.success) {
      if (resp.success.items && resp.success.items.length) {
        companies = resp.success.items.map((i: any) => {
          const weight = resp.success.weights[i.isin];
          i.weight = weight ? weight : 0;
          i.selected = true;
          return new CompanyModel(i);
        });
      }
      notFound = resp.success.not_found || [];
    }
    return {
      companies: companies,
      notFound: notFound,
    };
  }

  // -- Data fetching + tile mapping -----------------------------------------

  private fetch(): void {
    this.isLoading = true;
    this.hasError = false;
    this.hydratedIds.clear();
    this.inflightIds.clear();
    this.service.portfolioFinished.next({});
    this.service.getItems().subscribe({
      next: (resp: any) => {
        if (!resp || resp.error) {
          if (resp?.error) this.service.notificationService.open(resp.error);
          this.isLoading = false;
          this.hasError = true;
          this.all = [];
          this.filtered = [];
          this.visible = [];
          return;
        }
        const merged = this.mergeLists(resp);
        this.all = merged.map((raw) => this.toTile(raw));
        if (this.service.pendingComputingPortfolioId) {
          this.forcePortfolioStatus(this.service.pendingComputingPortfolioId, 'Computing');
          this.service.pendingComputingPortfolioId = null;
        }
        this.computeStats();
        this.applyFilters();
        this.isLoading = false;
        this.hydrateVisible();
      },
      error: () => {
        this.isLoading = false;
        this.hasError = true;
        this.all = [];
        this.filtered = [];
        this.visible = [];
      },
    });
  }

  private forcePortfolioStatus(id: number, status: string): void {
    const tile = this.all.find((p) => p.id === id);
    if (tile) tile.status = status;
  }

  private hydrateVisible(): void {
    const toFetch = this.visible.filter(
      (t) => !this.hydratedIds.has(t.id) && !this.inflightIds.has(t.id),
    );
    if (!toFetch.length) return;

    for (const tile of toFetch) {
      this.inflightIds.add(tile.id);
      tile.isLoadingSeries = true;

      this.service
        .fetchPerformancePlotSpark(tile.id)
        .pipe(catchError(() => of(null)))
        .subscribe((plot) => {
          this.inflightIds.delete(tile.id);
          this.hydratedIds.add(tile.id);
          const series = this.extractSeries(plot);
          if (series.length) tile.series = series;
          tile.isLoadingSeries = false;
          this.visible = [...this.visible];
        });
    }
    this.visible = [...this.visible];
  }

  private extractSeries(plot: any): number[] {
    const cum = plot?.cumulative_returns?.Portfolio;
    if (!Array.isArray(cum) || cum.length < 2) return [];
    const values: number[] = [];
    for (const entry of cum) {
      if (Array.isArray(entry) && typeof entry[1] === 'number') values.push(entry[1]);
    }
    if (!values.length) return [];
    const base = values[0] || 1;
    // The /performance-plot/spark endpoint already downsamples server-side,
    // so we just index the returned points to a 100 base for the sparkline.
    return values.map((v) => (v / base) * 100);
  }

  private toTile(raw: any): PortfolioTile {
    const summary = raw.performance_summary || {};
    const symPerf: any[] = raw.symbol_performance || [];
    const portfolioSym = symPerf.find((e) => e.Ticker === 'Portfolio');

    const annReturn =
      summary['Annualized Return']?.Portfolio ?? portfolioSym?.CAGR ?? 0;
    const vol = summary['Annualized Std Dev']?.Portfolio ?? portfolioSym?.Volatility ?? 0;

    const ccy = this.extractCurrency(raw);
    const status = this.extractStatus(raw);
    const assetCount =
      symPerf.filter((e) => e.Ticker !== 'Portfolio' && (e.Weight || 0) > 0).length ||
      Number(raw.security_count) ||
      0;

    const created = raw.created_at || raw.created || '';
    const updated = raw.last_updated_at || raw.updated_at || created;

    return {
      id: raw.id,
      name: raw.name || 'Untitled portfolio',
      status,
      ccy,
      assets: assetCount,
      starred: !!raw.is_starred,
      risk: (vol || 0) * 100,
      delta: (annReturn || 0) * 100,
      created: this.formatDate(created),
      updated: this.formatDate(updated),
      createdTs: this.parseTs(created),
      updatedTs: this.parseTs(updated),
      series: [],
      isLoadingSeries: false,
      readonly: !!raw.readonly,
      visibility: Number(raw.visibility) || 0,
      raw,
    };
  }

  private extractCurrency(raw: any): string {
    return (
      raw.currency?.code ||
      raw.currency_code ||
      (typeof raw.currency === 'string' ? raw.currency : null) ||
      raw.base_currency ||
      'USD'
    );
  }

  private extractStatus(raw: any): string {
    // Backend Portfolio status codes (api/app/Models/Portfolio.php):
    //   1 (STATUS_OPEN)     -> Computing
    //   2 (STATUS_PENDING)  -> Failed
    //   3 (STATUS_FINISHED) -> Ready
    // Only code 3 is truly Ready; anything else must not read as Ready.
    const code = Number(raw.status);
    if (code === 1) return 'Computing';
    if (code === 2) return 'Failed';
    if (code === 3) return 'Ready';
    // Fallback for any non-numeric status string the API might send.
    const s = (raw.status ?? raw.state ?? '').toString().toLowerCase();
    if (s.includes('comput') || s.includes('process') || s.includes('queue')) return 'Computing';
    if (s.includes('fail') || s.includes('error')) return 'Failed';
    return 'Ready';
  }

  private formatDate(raw: string | undefined): string {
    if (!raw) return '—';
    const ts = this.parseTs(raw);
    if (!ts) return '—';
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseTs(raw: string | undefined): number {
    if (!raw) return 0;
    const normalised =
      typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
    const ts = Date.parse(normalised);
    return isNaN(ts) ? 0 : ts;
  }

  private mergeLists(resp: any): any[] {
    const buckets = ['private', 'shared_by_me', 'shared_with_me'];
    const out: any[] = [];
    const seen = new Set<number>();
    for (const key of buckets) {
      const list = Array.isArray(resp?.[key]) ? resp[key] : [];
      for (const item of list) {
        if (!item || !item.id || seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(item);
      }
    }
    return out;
  }

  private computeStats(): void {
    const n = this.all.length;
    if (!n) {
      this.totalDelta = 0;
      this.avgRisk = 0;
      this.winners = 0;
      this.starredCount = 0;
      this.currencies = [];
      return;
    }
    this.totalDelta = this.all.reduce((s, p) => s + (p.delta || 0), 0) / n;
    this.avgRisk = this.all.reduce((s, p) => s + (p.risk || 0), 0) / n;
    this.winners = this.all.filter((p) => p.delta > 0).length;
    this.starredCount = this.all.filter((p) => p.starred).length;
    this.currencies = Array.from(new Set(this.all.map((p) => p.ccy))).sort();
  }

  private applyFilters(): void {
    const q = (this.search || '').trim().toLowerCase();
    let list = this.all.filter((p) => {
      if (this.favouritesOnly && !p.starred) return false;
      if (this.ccyFilter.size && !this.ccyFilter.has(p.ccy)) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.ccy.toLowerCase().includes(q)
      );
    });

    const dir = this.sortDir === 'desc' ? -1 : 1;
    list = [...list].sort((a, b) => {
      // Favourites are always pinned to the top, then the chosen sort applies.
      if (a.starred !== b.starred) return a.starred ? -1 : 1;
      switch (this.sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'return':
          return (a.delta - b.delta) * dir;
        case 'risk':
          return (a.risk - b.risk) * dir;
        case 'updated':
        default:
          return (a.updatedTs - b.updatedTs) * dir;
      }
    });

    this.filtered = list;
    this.visible = this.filtered.slice(0, this.visibleCount);
    this.hydrateVisible();
  }

  private computeGreeting(): string {
    const h = new Date().getHours();
    if (h < 5) return 'Working late';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }

  private resolveUserName(user: any): string {
    if (!user) return '';
    if (user.first_name && user.first_name.trim()) return user.first_name.trim();
    if (user.email) return String(user.email).split('@')[0];
    return '';
  }
}
